import React from 'react';
import PropTypes from 'prop-types';

const inputSizeClasses = {
  md: 'h-10 px-3 py-2 text-base',
  lg: 'h-12 px-4 py-3 text-base',
};

export const FormInput = ({
  label,
  helperText,
  error,
  success,
  required,
  inputSize = 'md',
  className = '',
  ...props
}) => {
  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        className={`
          w-full rounded-lg border transition-colors
          focus:outline-none focus:ring-2 focus:ring-offset-0
          disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
          ${inputSizeClasses[inputSize]}
          ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : success
                ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }
        `}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={
          error
            ? `${props.id || props.name}-error`
            : helperText
              ? `${props.id || props.name}-helper`
              : undefined
        }
        {...props}
      />
      {error && (
        <p
          className="mt-1 text-sm text-red-600 flex items-center gap-1"
          id={`${props.id || props.name}-error`}
          role="alert"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </p>
      )}
      {!error && success && (
        <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          {success}
        </p>
      )}
      {!error && !success && helperText && (
        <p
          className="mt-1 text-sm text-gray-500"
          id={`${props.id || props.name}-helper`}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

FormInput.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.string,
  success: PropTypes.string,
  required: PropTypes.bool,
  inputSize: PropTypes.oneOf(['md', 'lg']),
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export const Textarea = ({
  label,
  helperText,
  error,
  success,
  required,
  maxLength,
  showCount,
  rows = 4,
  className = '',
  value = '',
  onChange,
  ...props
}) => {
  const characterCount = value?.length || 0;

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <textarea
        rows={rows}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        className={`
          w-full rounded-lg border px-3 py-2 transition-colors resize-y
          focus:outline-none focus:ring-2 focus:ring-offset-0
          disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
          ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : success
                ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }
        `}
        aria-invalid={error ? 'true' : 'false'}
        {...props}
      />
      <div className="flex justify-between items-center mt-1">
        <div className="flex-1">
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </p>
          )}
          {!error && helperText && (
            <p className="text-sm text-gray-500">{helperText}</p>
          )}
        </div>
        {showCount && maxLength && (
          <p
            className={`text-sm ${
              characterCount > maxLength ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            {characterCount} / {maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

Textarea.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.string,
  success: PropTypes.string,
  required: PropTypes.bool,
  maxLength: PropTypes.number,
  showCount: PropTypes.bool,
  rows: PropTypes.number,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default FormInput;


